import { useEffect, useRef } from 'react';

interface AudioVisualizerProps {
  stream: MediaStream;
}

export function AudioVisualizer({ stream }: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !stream) return;

    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    source.connect(analyser);
    analyser.fftSize = 256;
    
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrame: number;
    let xOffset = 0;

    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;

      animationFrame = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const barWidth = 2;
      const gap = 2;
      const totalWidth = (barWidth + gap) * (bufferLength / 4);
      
      // Move the visualization to the left
      xOffset = (xOffset + 1) % (barWidth + gap);
      
      let x = -xOffset;

      for (let i = 0; i < bufferLength / 4; i++) {
        const barHeight = (dataArray[i] / 255) * height;
        const y = height - barHeight;
        
        if (x >= 0 && x < width) {
          ctx.fillStyle = 'rgb(239, 68, 68)'; // red-500
          ctx.fillRect(x, y, barWidth, barHeight);
        }
        
        x += barWidth + gap;
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrame);
      source.disconnect();
      audioContext.close();
    };
  }, [stream]);

  return (
    <canvas 
      ref={canvasRef} 
      width={96}
      height={32}
      className="w-full rounded-lg"
    />
  );
}